import React from 'react';
import { Card, Button, Input, Checkbox, message } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../../services/api';
import { TaskSubTask, SubTaskTemplate } from '../../services/tasks';
import { taskService } from '../../services/tasks';

interface User {
  id: number;
  name: string;
  email: string;
}

interface ChecklistTabProps {
  canEdit: boolean;
  canDelete: boolean;
  id?: string;
  taskSubTasks: TaskSubTask[];
  setTaskSubTasks: React.Dispatch<React.SetStateAction<TaskSubTask[]>>;
  subTaskTemplates: SubTaskTemplate[];
  setSubTaskTemplates: React.Dispatch<React.SetStateAction<SubTaskTemplate[]>>;
  isManualAdd: Set<string | number>;
  setIsManualAdd: React.Dispatch<React.SetStateAction<Set<string | number>>>;
  user?: User;
  formatDateTime: (dateString?: string) => string;
  getReferencePayload: () => Record<string, any>;
}

export const ChecklistTab: React.FC<ChecklistTabProps> = ({
  canEdit,
  canDelete,
  id,
  taskSubTasks,
  setTaskSubTasks,
  subTaskTemplates,
  setSubTaskTemplates,
  isManualAdd,
  setIsManualAdd,
  user,
  formatDateTime,
  getReferencePayload,
}) => {
  const allowDelete = canEdit && canDelete;
  const getCleanReferencePayload = () => {
    const referencePayload = getReferencePayload();
    return Object.fromEntries(
      Object.entries(referencePayload).filter(([, value]) => value !== undefined)
    );
  };
  return (
    <Card
      title="Sub-Tasks"
      bodyStyle={{ padding: '16px' }}
      extra={
        <Button
          type="link"
          icon={<PlusOutlined />}
          disabled={!canEdit}
          onClick={async () => {
            if (!canEdit) return;
            // If no sub task templates loaded, try to load them
            if (subTaskTemplates.length === 0) {
              try {
                const response = await api.get(`/sub-tasks`);
                const allSubTasks = response.data?.data || response.data || [];
                if (allSubTasks.length === 0) {
                  message.warning('No sub task templates available. Please create sub task templates first.');
                  return;
                }
                setSubTaskTemplates(allSubTasks);
              } catch {
                message.error('Failed to load sub task templates');
                return;
              }
            }
            
            // Add a new sub task from available templates
            const availableTemplates = subTaskTemplates.filter(
              st => !taskSubTasks.some(ts => ts.sub_task_template_id === st.id)
            );
            if (availableTemplates.length > 0) {
              const newId = `temp-${Date.now()}`;
              setTaskSubTasks(prev => [...prev, {
                id: newId,
                task_id: parseInt(id || '0', 10),
                sub_task_template_id: availableTemplates[0].id,
                name: '', // Empty name for manually added sub task
                completed: false,
                subTaskTemplate: availableTemplates[0],
              }]);
              setIsManualAdd(prev => new Set(prev).add(newId));
              message.success('Sub task added');
            } else {
              message.info('All available sub tasks have been added');
            }
          }}
        >
          Add
        </Button>
      }
    >
      {taskSubTasks.map((subTask, index) => (
        <div key={subTask.id || index} style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Checkbox
              checked={subTask.completed}
              disabled={!canEdit}
              onChange={async (e) => {
                if (!canEdit) return;
                const checked = e.target.checked;
                const now = new Date().toISOString();
                // Update local state immediately
                setTaskSubTasks(prev =>
                  prev.map(st =>
                    st.id === subTask.id
                        ? { 
                            ...st, 
                            completed: checked,
                            updated_at: checked ? now : st.updated_at,
                            updated_by: checked ? user?.id : st.updated_by,
                            updater: checked && user ? { id: user.id, name: user.name, email: user.email || '' } : st.updater,
                          }
                      : st
                  )
                );
                // If task is saved, update on backend
                if (canEdit && id && typeof subTask.id === 'number') {
                  try {
                    const referencePayload = getCleanReferencePayload();
                    await taskService.update(parseInt(id, 10), {
                      ...referencePayload,
                      sub_tasks: taskSubTasks.map(st =>
                        st.id === subTask.id
                          ? {
                              id: st.id,
                              sub_task_template_id: st.sub_task_template_id,
                              name: st.name,
                              completed: checked,
                            }
                          : {
                              id: typeof st.id === 'number' ? st.id : undefined,
                              sub_task_template_id: st.sub_task_template_id,
                              name: st.name,
                              completed: st.completed,
                            }
                      ).filter(st => typeof st.id === 'number' || st.sub_task_template_id),
                    });
                    // Reload task to get updated timestamps
                    const updatedTask = await taskService.getById(parseInt(id, 10));
                    const updatedSubTasks = (updatedTask as any).subTasks || (updatedTask as any).sub_tasks || [];
                    setTaskSubTasks(prev => prev.map(st => {
                      const updated = updatedSubTasks.find((ust: any) => ust.id === st.id);
                      return updated ? {
                        ...st,
                        updated_at: updated.updated_at,
                        updated_by: updated.updated_by,
                        updater: updated.updater,
                      } : st;
                    }));
                  } catch {
                    message.error('Failed to update sub task');
                  }
                }
              }}
            />
            <Input
              value={isManualAdd.has(subTask.id || '') ? (subTask.name || '') : (subTask.name || subTask.subTaskTemplate?.name || '')}
              disabled={!canEdit}
              onChange={(e) => {
                if (!canEdit) return;
                setTaskSubTasks(prev =>
                  prev.map(st =>
                    st.id === subTask.id
                      ? { ...st, name: e.target.value }
                      : st
                  )
                );
              }}
              style={{ flex: 1 }}
              placeholder={isManualAdd.has(subTask.id || '') ? 'Enter sub task name' : (subTask.subTaskTemplate?.name || 'Enter sub task name')}
            />
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              disabled={!allowDelete}
              onClick={() => {
                if (!allowDelete) return;
                setIsManualAdd(prev => {
                  const newSet = new Set(prev);
                  newSet.delete(subTask.id || '');
                  return newSet;
                });
                setTaskSubTasks(prev => prev.filter(st => st.id !== subTask.id));
              }}
            />
          </div>
          {subTask.completed && (
            <div style={{ marginLeft: 24, fontSize: '12px', color: '#999' }}>
              By {subTask.updater?.name || subTask.creator?.name || user?.name || 'Unknown'}{' '}
              {subTask.updated_at
                ? formatDateTime(subTask.updated_at)
                : subTask.created_at
                ? formatDateTime(subTask.created_at)
                : ''}
            </div>
          )}
        </div>
      ))}
      {taskSubTasks.length === 0 && (
        <div style={{ color: '#999', textAlign: 'center', padding: '20px 0' }}>
          No sub projects.tasks. Click "Add" to add a sub task or select a Task Template to load preset sub projects.tasks.
        </div>
      )}
    </Card>
  );
};
